import { useEffect } from 'react';

/**
 * SchemaOrg, injects a JSON-LD <script> tag into <head>.
 * Used for AI Search Optimization (AEO/GEO): Google AI Overviews, ChatGPT,
 * Perplexity, and Gemini use structured data as a strong citation signal.
 *
 * Usage:
 *   <SchemaOrg data={{"@context":"https://schema.org","@type":"Service",...}} />
 *
 * Data is cleaned up (script removed) when the component unmounts.
 */
export const SchemaOrg = ({ data, id }) => {
  useEffect(() => {
    if (!data) return undefined;

    const scriptId = id || `schema-${Math.random().toString(36).slice(2, 9)}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    return () => {
      const node = document.getElementById(scriptId);
      if (node) node.remove();
    };
  }, [data, id]);

  return null;
};

/* ================= Common schema builders ================= */

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Blue Ribbon Technologies',
  alternateName: 'BRT',
  url: 'https://blueribbontechnology.com',
  logo: 'https://blueribbontechnology.com/logo.png',
  description:
    'Blue Ribbon Technologies helps Canadian owner-led small and medium businesses adopt practical, budget-friendly AI, business automation, digital marketing, and product engineering.',
  foundingDate: '2021',
  areaServed: {
    '@type': 'Country',
    name: 'Canada',
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Vancouver',
    addressRegion: 'BC',
    addressCountry: 'CA',
  },
  sameAs: [
    'https://www.linkedin.com/company/blueribbon-technologies',
  ],
});

export const buildWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Blue Ribbon Technologies',
  url: 'https://blueribbontechnology.com',
  publisher: { '@type': 'Organization', name: 'Blue Ribbon Technologies' },
});

export const buildServiceSchema = ({ name, description, url, serviceType, areaServed = 'Canada' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: serviceType || name,
  name,
  description,
  url,
  provider: {
    '@type': 'Organization',
    name: 'Blue Ribbon Technologies',
    url: 'https://blueribbontechnology.com',
  },
  areaServed: { '@type': 'Country', name: areaServed },
});

export const buildFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: (items || []).map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export default SchemaOrg;
